import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, MenuItem, Modal, Stack, Typography, useTheme } from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { tableStyle } from "../../components";
import { tokens } from "../../theme";
import { Add, Edit } from "@mui/icons-material";
import Header from "../../components/Header";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import { CssTextField } from "../../components/TextField";
import { authorizedPost } from "../../store/actions";
import {
  ACTIVATE_PMS,
  ADD_NEW_PMS,
  GET_AVAILABLE_PMS,
  LIST_CONFIGURED_PMSS,
  UPDATE_PMS,
} from "../../graphql";
import { toast } from "react-toastify";

const HotelPmss = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Form state (used for both add + edit)
  const [selectedPmsId, setSelectedPmsId] = useState("");
  const [pmsAccessKey, setPmsAccessKey] = useState("");
  const [pmsUrlname, setPmsUrlname] = useState("");
  const [pmsPropertyId, setPmsPropertyId] = useState("");
  const [pmsGroupId, setPmsGroupId] = useState("");
  const [open, setOpen] = useState(false);
  // "add" | "edit"
  const [mode, setMode] = useState("add");

  // PMS dropdown options fetched from backend (get_available_pms)
  const [availablePmss, setAvailablePmss] = useState([]);
  const [pmsListLoading, setPmsListLoading] = useState(false);

  // Configured PMS table data (list_configured_pmss)
  const [pmssList, setPmssList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { sm: 400, xs: "90%" },
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: 24,
    p: 2,
  };

  useEffect(() => {
    document.title = 'PMSs';
    fetchConfiguredPmss();
    // eslint-disable-next-line
  }, []);

  const fetchConfiguredPmss = async () => {
    const response = await authorizedPost(LIST_CONFIGURED_PMSS);
    if (response?.status && response?.data?.list_configured_pmss?.data) {
      setPmssList(response.data.list_configured_pmss.data);
    }
  }

  const fetchAvailablePmss = async () => {
    setPmsListLoading(true);
    const response = await authorizedPost(GET_AVAILABLE_PMS);
    if (response?.status && response?.data?.get_available_pms?.data) {
      setAvailablePmss(response.data.get_available_pms.data);
    }
    setPmsListLoading(false);
  }

  const handleOpenAddModal = () => {
    setMode("add");
    resetForm();
    setOpen(true);
    fetchAvailablePmss();
  }

  const handleOpenEditModal = (row) => {
    setMode("edit");
    // Pre-populate the form with existing values
    setSelectedPmsId(row?.pms_id ?? "");
    setPmsAccessKey(row?.pms_access_key ?? "");
    setPmsUrlname(row?.pms_urlname ?? "");
    setPmsPropertyId(row?.pms_property_id ?? "");
    setPmsGroupId(row?.pms_group_id ?? "");
    setOpen(true);
    fetchAvailablePmss();
  }

  const isHotelier = availablePmss.find((p) => p.id === selectedPmsId)?.name?.toLowerCase() === "hotelier";

  const resetForm = () => {
    setSelectedPmsId("");
    setPmsAccessKey("");
    setPmsUrlname("");
    setPmsPropertyId("");
    setPmsGroupId("");
  }

  const handleClose = () => {
    setOpen(false);
    resetForm();
  }

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <Stack width="100%" alignItems="center">
          <Typography variant="h6">All datas listed</Typography>
        </Stack>
      </GridFooterContainer>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Explicit validation — all 4 fields are required
    if (!selectedPmsId) {
      toast.error("Please select a PMS");
      return;
    }
    if (!pmsAccessKey?.trim()) {
      toast.error("Access Key is required");
      return;
    }
    if (!pmsUrlname?.trim()) {
      toast.error("URL Name is required");
      return;
    }
    if (!pmsPropertyId?.trim()) {
      toast.error("Property ID is required");
      return;
    }
    if (isHotelier && !pmsGroupId?.trim()) {
      toast.error("Group ID is required for Hotelier");
      return;
    }

    const payload = {
      pmsId: String(selectedPmsId),
      pmsAccessKey: pmsAccessKey.trim(),
      pmsUrlname: pmsUrlname.trim(),
      pmsPropertyId: pmsPropertyId.trim(),
      pmsGroupId: pmsGroupId.trim() || undefined,
    };

    setIsLoading(true);
    const mutation = mode === "edit" ? UPDATE_PMS : ADD_NEW_PMS;
    const response = await authorizedPost(mutation, payload);
    setIsLoading(false);

    if (response?.status) {
      const responseKey = mode === "edit" ? "update_pms" : "add_new_pms";
      const fallback = mode === "edit" ? "PMS updated successfully" : "PMS added successfully";
      const message = response?.data?.[responseKey]?.message || fallback;
      toast.success(message);
      resetForm();
      setOpen(false);
      fetchConfiguredPmss();
    }
    // Errors are already toast'd globally via api.config.js
  }

  const handleToggleActivation = async () => {
    const response = await authorizedPost(ACTIVATE_PMS);
    if (response?.status) {
      const message = response?.data?.activate_pms?.message || "PMS updated";
      toast.success(message);
      fetchConfiguredPmss();
    }
  }

  const columns = [
    {
      field: "pms_name",
      headerName: "PMS Name",
      width: 160,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { pms_name } }) => pms_name || "-",
    },
    {
      field: "pms_access_key",
      headerName: "Access Key",
      width: 200,
      renderCell: ({ row: { pms_access_key } }) => pms_access_key || "-",
    },
    {
      field: "pms_urlname",
      headerName: "URL Name",
      width: 200,
      renderCell: ({ row: { pms_urlname } }) => pms_urlname || "-",
    },
    {
      field: "pms_property_id",
      headerName: "Property ID",
      width: 160,
      renderCell: ({ row: { pms_property_id } }) => pms_property_id || "-",
    },
    {
      field: "pms_group_id",
      headerName: "Group ID",
      width: 130,
      renderCell: ({ row: { pms_group_id } }) => pms_group_id || "-",
    },
    {
      field: "pms_is_active",
      headerName: "Availability",
      width: 130,
      renderCell: ({ row: { pms_is_active } }) => {
        return (
          <AvailabilityUpdation
            isActive={pms_is_active}
            action={handleToggleActivation}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<Edit fontSize="small" />}
          onClick={() => handleOpenEditModal(row)}
        >
          Update
        </Button>
      ),
    },
  ];

  // Hide "Add" button when a PMS is already configured (one PMS per property)
  const hasMapping = pmssList?.length > 0;

  return (
    <Box m="20px" mb={15}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        m="20px 0"
      >
        <Header title="PMSS" subtitle="welcome to PMSs" />
        {!hasMapping && (
          <Box>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: { sm: "10px 20px", xs: "5px 10px" },
              }}
              onClick={handleOpenAddModal}
            >
              <Add sx={{ mr: "10px" }} />
              Add new PMS
            </Button>
          </Box>
        )}
      </Stack>
      <Box
        m="8px 0 0 0"
        height="68vh"
        sx={tableStyle(colors)}
      >
        <DataGrid
          rows={pmssList}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row?._id}
        />
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
      >
        <Box sx={modalStyle}>
          <Header
            title={mode === "edit" ? "EDIT PMS" : "ADD NEW PMS"}
            titleVariant="h4"
          />
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <CssTextField
                fullWidth
                required
                select
                size="small"
                label="PMS"
                value={selectedPmsId ?? ""}
                onChange={(e) => setSelectedPmsId(e.target.value)}
                disabled={pmsListLoading}
              >
                {pmsListLoading ? (
                  <MenuItem value="" disabled>Loading...</MenuItem>
                ) : availablePmss?.length ? (
                  availablePmss.map((pms) => (
                    <MenuItem value={pms.id} key={pms.id}>
                      {pms.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>No PMSs available</MenuItem>
                )}
              </CssTextField>
              <CssTextField
                fullWidth
                required
                size="small"
                type="text"
                label="Access Key"
                placeholder="Access Key"
                value={pmsAccessKey ?? ""}
                onChange={(e) => setPmsAccessKey(e.target.value)}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="text"
                label="URL Name"
                placeholder="URL Name"
                value={pmsUrlname ?? ""}
                onChange={(e) => setPmsUrlname(e.target.value)}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="text"
                label="Property ID"
                placeholder="Property ID"
                value={pmsPropertyId ?? ""}
                onChange={(e) => setPmsPropertyId(e.target.value)}
              />
              {isHotelier && (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="text"
                  label="Group ID"
                  placeholder="Group ID (from Hotelier hotel details)"
                  value={pmsGroupId ?? ""}
                  onChange={(e) => setPmsGroupId(e.target.value)}
                />
              )}
              <Button
                fullWidth
                size="large"
                type="submit"
                color="secondary"
                variant="contained"
                sx={{ fontSize: "15px" }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress color="inherit" size={24} /> : <b>{mode === "edit" ? "Update" : "Submit"}</b>}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default HotelPmss;
